<template>
  <table class="min-w-full">
    <thead class="bg-gray-50">
      <tr class="first-child:pl-3 last-child:pr-3 child:py-2 sticky top-0 text-xs child:border-b child:border-gray-200 font-semibold text-gray-600">
        <th class="text-left uppercase tracking-wider bg-gray-50">
          Contract Works
        </th>

        <th class="text-right uppercase tracking-wider bg-gray-50 ">
          Subcontract Sum
        </th>

        <th class="text-right uppercase tracking-wider bg-gray-50 pr-3">
          Rate
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          Current Claim
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          Current Certified
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          Variance
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          Previous
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50 pr-3">
          Cumulative
        </th>

        <th class="text-right uppercase tracking-wider bg-blue-50">
          <div class="flex items-center justify-end space-x-2">
            <button
              type="button"
              class="btn-icon btn-icon-default"
              :title="showPercentages ? 'Hide Percentages' : 'Show Percentages'"
              @click.stop="showPercentages = !showPercentages"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"
                :class="[{'rotate-180': !showPercentages}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
            </button>

            <span v-if="showPercentages">Current Claim %</span>
          </div>
        </th>

        <th
          v-if="showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50"
        >
          Current Certified %
        </th>

        <th
          v-if="showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50"
        >
          Previous %
        </th>

        <th
          v-if="showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50 pr-3"
        >
          Cumulative %
        </th>

        <th class="text-right uppercase tracking-wider bg-gray-50 pr-3">
          Cost To Complete
        </th>

        <th
          class="text-right uppercase tracking-wider bg-orange-200/30 relative z-10"
          title="Forecast total for complete works"
        >
          <div class="flex items-center justify-end space-x-2">
            <button
              type="button"
              class="btn-icon btn-icon-default"
              :title="showForecasts ? 'Hide Forecasts' : 'Show Forecasts'"
              @click.stop="showForecasts = !showForecasts"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"
                :class="[{'rotate-180': !showForecasts}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
            </button>

            <span v-if="showForecasts">Baseline Forecast</span>
          </div>
        </th>

        <th
          v-if="showForecasts"
          class="text-right uppercase tracking-wider bg-orange-200/30 relative z-10 pr-3"
          title="Forecast for the period"
        >
          Actual Forecast
        </th>
      </tr>
    </thead>

    <tbody
      v-if="claim"
      class="bg-white divide-y divide-gray-200"
    >
      <RatesTradeRow
        v-for="(trade, i) in trades"
        :key="i"
        :trade="trade.trade"
        :claim="claim"
        :project-id="projectId"
        :package-id="packageId"
        :can-edit-certified="canEditCertified"
        :package-trade-items="trade.packageTradeItems"
        :lite-mode="props.liteMode"
        :show-percentages="showPercentages"
        :show-forecasts="showForecasts"
      />

      <!-- Subtotal Row -->
      <tr class="first-child:pl-3 last-child:pr-3 child:py-4 bg-gray-50 border-t-2 border-gray-200">
        <td class="font-semibold text-gray-900">
          Subtotal
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.trades.budget) }}
        </td>

        <td />

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.trades.current_quantity) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.trades.current_certified) }}
        </td>

        <td
          class="text-right font-semibold"
          :class="{ 'text-red-600': totalCosts.trades.variance !== 0, 'text-gray-900': totalCosts.trades.variance === 0 }"
        >
          {{ australianCurrency(totalCosts.trades.variance) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.trades.previous) }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.trades.cumulativeCertified) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          <span v-if="showPercentages">
            {{ isNumber(totalCosts.trades.current_quantity / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.current_quantity / totalCosts.trades.budget) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ isNumber(totalCosts.trades.current_certified / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.current_certified / totalCosts.trades.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ isNumber(totalCosts.trades.previous / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.previous / totalCosts.trades.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900 pr-3"
        >
          {{ isNumber(totalCosts.trades.cumulativeCertified / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.cumulativeCertified / totalCosts.trades.budget) * 100) : 0 }}%
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(Math.abs(totalCosts.trades.budget - totalCosts.trades.cumulativeCertified)) }}
        </td>

        <td class="text-right font-semibold text-gray-900 relative z-10">
          <span v-if="showForecasts">{{ australianCurrency(totalCosts.trades.finalForecast) }}</span>
        </td>

        <td
          v-if="showForecasts"
          class="text-right font-semibold text-gray-900 relative z-10 pr-3"
        >
          {{ australianCurrency(totalCosts.trades.currentForecast) }}
        </td>
      </tr>

      <template
        v-for="(orders, key) in variationOrdersByType"
        :key="key"
      >
        <VariationOrderRow
          :claim="claim"
          :variation-orders="orders"
          :title="VariationOrderTitles[key]"
          :variation-order-type="VariationOrderType[key]"
          :can-edit-certified="canEditCertified"
          :lite-mode="props.liteMode"
          :project-id="props.projectId"
          :show-percentages="showPercentages"
          :show-forecasts="showForecasts"
        />
      </template>

      <!-- Variation Orders Subtotal Row -->
      <tr class="first-child:pl-3 last-child:pr-3 child:py-4 bg-gray-50 border-t-2 border-gray-200">
        <td class="font-semibold text-gray-900">
          Subtotal
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.variationOrders.budget) }}
        </td>

        <td />

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.variationOrders.current_claim) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.variationOrders.current_certified) }}
        </td>

        <td
          class="text-right font-semibold"
          :class="{ 'text-red-600': totalCosts.variationOrders.variance !== 0, 'text-gray-900': totalCosts.variationOrders.variance === 0 }"
        >
          {{ australianCurrency(totalCosts.variationOrders.variance) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.variationOrders.previous) }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.variationOrders.cumulativeCertified) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          <span v-if="showPercentages">
            {{ totalCosts.variationOrders.budget > 0 ? australianNumber((totalCosts.variationOrders.current_claim / totalCosts.variationOrders.budget) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ totalCosts.variationOrders.budget > 0 ? australianNumber((totalCosts.variationOrders.current_certified / totalCosts.variationOrders.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ totalCosts.variationOrders.budget > 0 ? australianNumber((totalCosts.variationOrders.previous / totalCosts.variationOrders.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900 pr-3"
        >
          {{ totalCosts.variationOrders.budget > 0 ? australianNumber((totalCosts.variationOrders.cumulativeCertified / totalCosts.variationOrders.budget) * 100) : 0 }}%
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.variationOrders.budget - totalCosts.variationOrders.cumulativeCertified) }}
        </td>

        <td
          class="text-right font-semibold text-gray-900 relative z-10"
          :class="[showForecasts ? 'bg-orange-200/30' : '']"
        >
          <span v-if="showForecasts">{{ australianCurrency(totalCosts.variationOrders.finalForecast) }}</span>
        </td>

        <td
          v-if="showForecasts"
          class="text-right font-semibold text-gray-900 bg-orange-200/30 relative z-10 pr-3"
        >
          {{ australianCurrency(totalCosts.variationOrders.currentForecast) }}
        </td>
      </tr>

      <DayworksSection
        v-if="claim && hasDayworks"
        :claim="claim"
        :can-edit-certified="canEditCertified"
        :lite-mode="props.liteMode"
        :project-id="props.projectId"
        :show-percentages="showPercentages"
        :show-forecasts="showForecasts"
      />

      <!-- Dayworks Subtotal Row -->
      <tr
        v-if="hasDayworks"
        class="first-child:pl-3 last-child:pr-3 child:py-4 bg-gray-50 border-t-2 border-gray-200"
      >
        <td class="font-semibold text-gray-900">
          Dayworks Subtotal
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.dayworks.calculated) }}
        </td>

        <td />

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.dayworks.current_claim) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.dayworks.current_certified) }}
        </td>

        <td
          class="text-right font-semibold"
          :class="{ 'text-red-600': totalCosts.dayworks.variance !== 0, 'text-gray-900': totalCosts.dayworks.variance === 0 }"
        >
          {{ australianCurrency(totalCosts.dayworks.variance) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.dayworks.previous) }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.dayworks.cumulativeCertified) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          <span v-if="showPercentages" />
        </td>

        <td
          v-if="showPercentages"
          colspan="3"
        />

        <td class="pr-3" />

        <td
          class="relative z-10"
          :class="[showForecasts ? 'bg-orange-200/30' : '']"
        />

        <td
          v-if="showForecasts"
          class="bg-orange-200/30 relative z-10 pr-3"
        />
      </tr>

      <!-- Grand Total Row -->
      <tr class="first-child:pl-3 last-child:pr-3 child:py-4 bg-blue-50 border-t-2 border-blue-200 sticky bottom-0">
        <td class="font-bold text-blue-900">
          Grand Total
        </td>

        <td class="text-right font-bold text-blue-900 pr-3">
          {{ australianCurrency(totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.dayworks.calculated) }}
        </td>

        <td />

        <td class="text-right font-bold text-blue-900">
          {{ australianCurrency(totalCosts.variationOrders.current_claim + totalCosts.trades.current_quantity + totalCosts.dayworks.current_claim) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ australianCurrency(totalCosts.variationOrders.current_certified + totalCosts.trades.current_certified + totalCosts.dayworks.current_certified) }}

          <p class="text-gray-500">
            Payable: {{ $tcc(claim.totalPayable) }}
          </p>
        </td>

        <td
          class="text-right font-bold"
          :class="{ 'text-red-600': totalCosts.variationOrders.variance + totalCosts.trades.variance + totalCosts.dayworks.variance !== 0, 'text-blue-900': totalCosts.variationOrders.variance + totalCosts.trades.variance + totalCosts.dayworks.variance === 0 }"
        >
          {{ australianCurrency(totalCosts.variationOrders.variance + totalCosts.trades.variance + totalCosts.dayworks.variance) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ australianCurrency(totalCosts.variationOrders.previous + totalCosts.trades.previous + totalCosts.dayworks.previous) }}
        </td>

        <td class="text-right font-bold text-blue-900 pr-3">
          {{ australianCurrency(totalCosts.variationOrders.cumulativeCertified + totalCosts.trades.cumulativeCertified + totalCosts.dayworks.cumulativeCertified) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          <span v-if="showPercentages">
            {{ totalCosts.variationOrders.budget + totalCosts.trades.budget > 0 ? australianNumber(((totalCosts.variationOrders.current_claim + totalCosts.trades.current_quantity) / (totalCosts.variationOrders.budget + totalCosts.trades.budget)) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-bold text-blue-900"
        >
          {{ totalCosts.variationOrders.budget + totalCosts.trades.budget > 0 ? australianNumber(((totalCosts.variationOrders.current_certified + totalCosts.trades.current_certified) / (totalCosts.variationOrders.budget + totalCosts.trades.budget)) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-bold text-blue-900"
        >
          {{ totalCosts.variationOrders.budget + totalCosts.trades.budget > 0 ? australianNumber(((totalCosts.variationOrders.previous + totalCosts.trades.previous) / (totalCosts.variationOrders.budget + totalCosts.trades.budget)) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-bold text-blue-900 pr-3"
        >
          {{ totalCosts.variationOrders.budget + totalCosts.trades.budget > 0 ? australianNumber(((totalCosts.variationOrders.cumulativeCertified + totalCosts.trades.cumulativeCertified) / (totalCosts.variationOrders.budget + totalCosts.trades.budget)) * 100) : 0 }}%
        </td>

        <td class="text-right font-bold text-blue-900 pr-3">
          {{ australianCurrency(totalCosts.variationOrders.budget - totalCosts.variationOrders.cumulativeCertified + totalCosts.trades.budget - totalCosts.trades.cumulativeCertified) }}
        </td>

        <td
          class="text-right font-bold text-blue-900 relative z-10"
        >
          <span v-if="showForecasts">{{ australianCurrency(totalCosts.variationOrders.finalForecast + totalCosts.trades.finalForecast) }}</span>
        </td>

        <td
          v-if="showForecasts"
          class="text-right font-bold text-blue-900 relative z-10 pr-3"
        >
          {{ australianCurrency(totalCosts.variationOrders.currentForecast + totalCosts.trades.currentForecast) }}
        </td>
      </tr>
    </tbody>

    <ScopeGapTable
      v-if="claim?.scope_gap"
      :scope-gap-id="claim.scope_gap.id"
      :project-id="projectId"
      :package-id="packageId"
      :can-edit="!props.isReadOnly"
      :package-total="totalCosts.variationOrders.budget + totalCosts.trades.budget"
      :show-percentages="showPercentages"
      :show-forecasts="showForecasts"
      :rates-column="true"
      :actual-forecast-sum="totalCosts.variationOrders.currentForecast + totalCosts.trades.currentForecast"
      @draft-added="$emit('scrollContainer')"
    />
  </table>
</template>

<script setup lang="ts">
import { australianCurrency, australianNumber, isNumber } from '@/helpers';
import { ClaimStatus } from '@/models/Claim';
import Labour from '@/models/Labour';
import PackageTradeItem from '@/models/PackageTradeItem';
import Plant from '@/models/Plant';
import Trade from '@/models/Trade';
import VariationOrder, {
  VariationOrderStatus,
  VariationOrderTitles,
  VariationOrderType,
} from '@/models/VariationOrder';
import { useClaimsStore } from '@/store/claims';
import { useDayworksCostsStore } from '@/store/dayworksCosts';
import { useLaboursStore } from '@/store/labours';
import { usePackagesStore } from '@/store/packages';
import { usePackageTradeItemsStore } from '@/store/packageTradeItems';
import { usePlantsStore } from '@/store/plants';
import { useRatesCostsStore } from '@/store/ratesCosts';
import { useTradesStore } from '@/store/trades';
import { useUserStore } from '@/store/user';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { Collection, useRepo } from 'pinia-orm';
import { computed, ref } from 'vue';
import DayworksSection from './DayworksSection.vue';
import RatesTradeRow from './RatesTradeRow.vue';
import ScopeGapTable from './ScopeGapTable.vue';
import VariationOrderRow from './VariationOrderRow.vue';

const props = defineProps<{
  claimId: string;
  projectId: string;
  packageId: string;
  liteMode?: boolean;
  isReadOnly?: boolean;
}>();

const emits = defineEmits<{
  (e: 'scrollContainer'): void;
}>();

const showPercentages = ref(false);
const showForecasts = ref(false);
const claimsStore = useClaimsStore();
const userStore = useUserStore();
const packagesStore = usePackagesStore();
const packageTradeItemsStore = usePackageTradeItemsStore();
const laboursStore = useLaboursStore();
const plantsStore = usePlantsStore();

const claim = computed(() => {
  if(props.claimId) {
    const claimQuery = claimsStore.models
      .with('trades', (query) => {
        query.with('trade_items')
          .orderBy('sort_order');
      })
      .with('variation_orders', (query) => {
        query.whereIn('status', [
          VariationOrderStatus.approved,
          VariationOrderStatus.principled,
          VariationOrderStatus.completed,
        ])
          .with('trade_items')
          .where('package_id', parseInt(props.packageId));
      })
      .with('rates_costs', (query) => {
        query.with('package_trade_item');
      })
      .with('variation_order_costs')
      .with('dayworks_costs', (query) => {
        query.with('labour').with('plant');
      })
      .with('scope_gap')
      .where(
        'project_id',
        parseInt(props.projectId),
      );

    return claimQuery.find(parseInt(props.claimId));
  }

  return undefined;
});

const packageTradeItems = computed(() => {
  return packageTradeItemsStore.models
    .where('package_id', parseInt(props.packageId))
    .with('trade_item', (query) => {
      query.with('trade');
    })
    .with('rates_costs', (query) => query.where('claim_id', parseInt(props.claimId)))
    .orderBy('sort_order', 'asc')
    .get();
});

const trades = computed(() => {
  const trades = new Map<Id, { trade?: Trade; packageTradeItems: PackageTradeItem[] }>();

  trades.set('Package', {
    trade: undefined,
    packageTradeItems: [],
  });

  packageTradeItems.value.forEach((packageTradeItem) => {
    if(!packageTradeItem.trade_item) {
      return;
    }

    const tradeKey = packageTradeItem.trade_item.trade?.id ?? 'Package';
    const trade = trades.get(tradeKey);

    if(trade) {
      trade.packageTradeItems.push(packageTradeItem);
    } else {
      trades.set(tradeKey, {
        trade: packageTradeItem.trade_item.trade,
        packageTradeItems: [packageTradeItem],
      });
    }
  });

  if(trades.get('Package').packageTradeItems.length === 0) {
    trades.delete('Package');
  }

  return Array.from(trades.values());
});

const subcontractorPackage = computed(() => {
  return packagesStore.models
    .with('package_trade_items', (query) => {
      query.with('rates_costs', (query) => {
        query.where('claim_id', parseInt(props.claimId));
      });
    }).find(parseInt(props.packageId));
});

const canEditCertified = computed(() => {
  // Only allow editing certified amounts if the claim is in draft status
  // and the user has the appropriate permissions
  return ((claim.value?.status === ClaimStatus.draft && props.liteMode) ||
    (claim.value?.status === ClaimStatus.subcontractor_claim)) && !userStore.isClientManager();
});

const variationOrdersStore = useVariationOrdersStore();

const variationOrdersByType = computed(() => {
  const costs: Partial<{ [k in keyof typeof VariationOrderType]: Collection<VariationOrder> }> = {};

  for(const key in VariationOrderType) {
    const variationOrders = variationOrdersStore.models
      .where('project_id', parseInt(props.projectId))
      .where('package_id', parseInt(props.packageId))
      .whereIn(
        'status',
        [VariationOrderStatus.approved, VariationOrderStatus.principled, VariationOrderStatus.completed],
      )
      .where(
        'type',
        VariationOrderType[key],
      ).with('costs')
      .with('trade_items')
      .with('package_trade_item')
      .get();

    // Completed or transferred variation orders that have a cost on this claim
    const historicalVariationOrders = variationOrdersStore.models
      .where('project_id', parseInt(props.projectId))
      .where('package_id', parseInt(props.packageId))
      .where((variationOrder) => {
        return variationOrder.isTransferred;
      })
      .whereHas('costs', (query) => {
        query.where('claim_id', parseInt(props.claimId));
      })
      .where('type', VariationOrderType[key])
      .with('costs')
      .with('trade_items')
      .with('package_trade_item')
      .get();

    costs[key] = variationOrders
      .concat(historicalVariationOrders)
      .sort((a, b) => a.CodeRef.localeCompare(b.CodeRef));
  }

  return costs;
});

const dayworksCostsStore = useDayworksCostsStore();

const dayworksCosts = computed(() => {
  return dayworksCostsStore.models
    .where('claim_id', parseInt(props.claimId))
    .with('labour')
    .with('plant')
    .get();
});

const labours = computed(() => {
  return laboursStore.models.where('$claim_id', parseInt(props.claimId)).get();
});

const plants = computed(() => {
  return plantsStore.models.where('$claim_id', parseInt(props.claimId)).get();
});

const hasDayworks = computed(() => {
  return labours.value.length > 0 || plants.value.length > 0 || dayworksCosts.value.length > 0;
});

const totalCosts = computed(() => {
  const totals = {
    trades: {
      budget: 0,
      current_quantity: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
      variance: 0,
      finalForecast: 0,
      currentForecast: 0,
    },

    variationOrders: {
      budget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
      variance: 0,
      finalForecast: 0,
      currentForecast: 0,
    },

    dayworks: {
      calculated: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
      variance: 0,
    },
  };

  if(subcontractorPackage.value) {
    subcontractorPackage.value.package_trade_items.forEach((packageTradeItem) => {
      totals.trades.budget += packageTradeItem.value;
      totals.trades.previous += packageTradeItem.previous(claim.value) * packageTradeItem.rate;
      totals.trades.finalForecast += packageTradeItem.baseline_forecast_amount || 0;

      const forecastAmounts = packageTradeItem.actual_forecast_amount || [];

      if(forecastAmounts.length > 0) {
        totals.trades.currentForecast += forecastAmounts[forecastAmounts.length - 1] || 0;
      }

      packageTradeItem.rates_costs.forEach((cost) => {
        totals.trades.current_quantity += (cost.current_quantity || 0) * packageTradeItem.rate;
        totals.trades.current_certified += (cost.current_certified || 0) * packageTradeItem.rate;
      });
    });
  }

  if(claim.value) {
    for(const key in variationOrdersByType.value) {
      variationOrdersByType.value[key].forEach((variationOrder) => {
        totals.variationOrders.budget += variationOrder.totalBudget;

        totals.variationOrders.previous += variationOrder.previous(claim.value);
        totals.variationOrders.finalForecast += variationOrder.baseline_forecast_amount || 0;

        const forecastAmounts = variationOrder.actual_forecast_amount || [];

        if(forecastAmounts.length > 0) {
          totals.variationOrders.currentForecast += forecastAmounts[forecastAmounts.length - 1] || 0;
        }
      });
    }

    claim.value.variation_order_costs?.forEach((variationOrderCost) => {
      totals.variationOrders.current_claim += variationOrderCost.current_claim || 0;
      totals.variationOrders.current_certified += variationOrderCost.current_certified || 0;
      totals.variationOrders.variance += variationOrderCost.current_claim - variationOrderCost.current_certified;
    });

    claim.value?.dayworks_costs?.forEach((dayworksCost) => {
      totals.dayworks.current_claim += dayworksCost.current_claim || 0;
      totals.dayworks.current_certified += dayworksCost.current_certified || 0;
      totals.dayworks.variance += (dayworksCost.current_claim || 0) - (dayworksCost.current_certified || 0);
    });

    labours.value.forEach((labour) => {
      totals.dayworks.calculated += labour.calculatedAmount || 0;
      totals.dayworks.previous += labour.$previous || 0;
    });

    plants.value.forEach((plant) => {
      totals.dayworks.calculated += plant.calculatedAmount || 0;
      totals.dayworks.previous += plant.$previous || 0;
    });
  }

  totals.trades.cumulativeCertified = totals.trades.current_certified + totals.trades.previous;
  totals.trades.variance = totals.trades.current_quantity - totals.trades.current_certified;

  totals.variationOrders.cumulativeCertified = totals.variationOrders.current_certified +
    totals.variationOrders.previous;

  totals.dayworks.cumulativeCertified = totals.dayworks.current_certified + totals.dayworks.previous;

  return totals;
});
</script>

<style scoped></style>
